import React from 'react'
import axios from 'axios'
import classNames from 'classnames'
import Tooltip from '@mui/material/Tooltip'
import Icon from '../../shared/icon'
import { t } from 'src/i18n'
import { HUMAN_SLOTS } from '../types'

const scope = 'replenishment.sidebar'

const ProductOrderItem = ({
  orderItem, index, showRemove, cancelable, cancelItem, removeReplenishment, weightScale, onViewDishes
 }) => {

  const actionName = () => {
    return t(isRefill() ? 'refill' : 'product_change', { scope })
  }

  const isRefill = () => {
    return orderItem.product?.id == orderItem.previousProduct?.id
  }

  const showPreviousProduct = () => {
    return !isRefill()
  }

  const itemClassNames = classNames(
    'replenishment-order-item',
    {
      'with-previous-product': showPreviousProduct(),
      'first': index === 0,
    }
  )

  const productCancelable = () => {
    return cancelable && ['not_started', 'awaiting_verification', 'verification_processing', 'invalid'].includes(orderItem.state)
  }

  const renderFeederSubtypeIcon = (feederSubtype: string) => {
    return feederSubtype == 'fridge' ? <Icon name='fridge' color='#2D5CF2' /> : <Icon name='container' />
  }

  const productIsPresent = () => {
    return orderItem.product && orderItem.product.id
  }

  const renderStorageLocation = () => {
    if (!productIsPresent()) return null

    return (
      <div className="storage-location">
        {renderFeederSubtypeIcon(orderItem.product.feeder_subtype)}
        <span>{!!orderItem.product.feeder_subtype ? t(`feeder_subtypes.${orderItem.product.feeder_subtype}`, { scope: 'product' }) : 'Container'} | {actionName()}</span>
      </div>
    )
  }

  const renderViewDishesLink = () => {
    if (!productIsPresent() || !onViewDishes) return null

    return (
      <a href="#" className="view-dishes-link" onClick={(e) => {
        e.preventDefault()
        onViewDishes?.(orderItem.product.id, orderItem.product.name)
      }}>
        <Icon name='external' />
        {t('view_dishes_with_ingredient', { scope })}
      </a>
    )
  }

  return (
      <div className={`${itemClassNames}`}>
        <div className="image">
          {showPreviousProduct() && <img className="old-product-img" src={orderItem.previousProduct?.imageUrl || 'images/product-placeholder.svg'}></img>}
          <img className="new-product-img" src={orderItem.product.imageUrl || '/images/missing_image.svg'}/>
          <span className="slot-number">#{orderItem.slotNumber}</span>
        </div>
        <div className="info">
          <div className="product-header">
            <div className="new-product">
              <div className="new-product-name"> {orderItem.product.name || t('empty_container', { scope })} </div>
              <div className="replenishment-type-badge-container">
                <div
                  className={`replenishment-type-badge replenishment-type-badge-${orderItem.replenishmentType}`}>
                  {t(`replenishment_types.${orderItem.replenishmentType}`, { scope })}
                </div>
              </div>
            </div>
          </div>
          {renderStorageLocation()}
          {renderViewDishesLink()}
          {!showRemove && (
            <span className="status-text">{actionName()} | {orderItem.state.replace("_", " ")}</span>
          )}
        </div>
        {showRemove &&
          <div className="delete-item">
            <Icon name='deleteIcon' onClick={() => removeReplenishment(orderItem.id, 'product')}/>
          </div>
        }
        {productCancelable() && !showRemove &&
          <Tooltip title={t('cancel')} placement='top' arrow>
            <div className="cancel-item">
              <Icon name='deleteIcon' className='close-icon' onClick={() => cancelItem(orderItem, 'product')}/>
            </div>
          </Tooltip>
        }
      </div>
  )
}

const PlatesOrderItem = ({
  orderItem, cancelItem, index, showRemove, cancelable, removeReplenishment, weightScale
 }) => {

  const itemClassNames = classNames(
    'replenishment-order-item',
    {
      'first': index === 0,
    }
  )

  const itemCancelable = () => {
    return cancelable && orderItem.state == 'not_started'
  }

  return (
    <div className={`${itemClassNames}`}>
      <div className="image">
        <div className="plates-icon-container">
          <Icon name='plate' color='white'/>
        </div>
        <span className="slot-number">#{HUMAN_SLOTS[orderItem.slotNumber - 1]}</span>
      </div>
      <div className="info">
        <span className="new-product">{t('replenishment.bowls.bowl_stand', {slotNumber: HUMAN_SLOTS[orderItem.slotNumber - 1]})}</span>
        <span className="action-name">{orderItem.amount} pcs refill | {orderItem.state.replace("_", " ")}</span>
      </div>
      {showRemove &&
        <div className="delete-item">
         <Icon name='deleteIcon' onClick={() => removeReplenishment(orderItem.id, 'plates')}/>
        </div>
      }
      {itemCancelable() && !showRemove &&
          <Tooltip title={t('cancel')} placement='top' arrow>
            <div className="cancel-item">
              <Icon name='deleteIcon' className='close-icon' onClick={() => cancelItem(orderItem, 'plates')}/>
            </div>
          </Tooltip>
        }
      <hr/>
    </div>
)
}

const OilOrderItem = ({
  orderItem, cancelItem, showRemove, cancelable, removeReplenishment, weightScale
}) => {

  const itemCancelable = () => {
    return cancelable && orderItem.state == 'not_started'
  }

  return (
    <div className='replenishment-order-item first oil-item'>
      <div className="image">
        <div className="plates-icon-container">
          <Icon name='oil_tank' color='white'/>
        </div>
      </div>
      <div className="info">
        <span className="new-product">Oil tank</span>
        <span className="action-name">
          Refill
        </span>
      </div>
      {showRemove &&
        <div className="delete-item">
         <Icon name='deleteIcon' onClick={() => removeReplenishment(orderItem.id, 'oil')}/>
        </div>
      }
      {itemCancelable() && !showRemove &&
          <Tooltip title={t('cancel')} placement='top' arrow>
            <div className="cancel-item">
              <Icon name='deleteIcon' className='close-icon' onClick={() => cancelItem(orderItem, 'oil')}/>
            </div>
          </Tooltip>
        }
      <hr/>
    </div>
  )
}

export const ReplenishmentOrderItem = ({
  orderItem, index, type, weightScale = null, cancelItem = () => {}, showRemove = true,
  cancelable=false, removeReplenishment = (replenishmentItemId, type) => {},
  onViewDishes = undefined as ((productId: number, productName: string) => void) | undefined }
) => {
  const props = {
    orderItem,
    index,
    showRemove,
    cancelable,
    cancelItem,
    removeReplenishment,
    type,
    weightScale,
    onViewDishes
  }

  if(type == 'product') {
    return (<ProductOrderItem {...props}/>)
  } else if (type == 'plates') {
    return (<PlatesOrderItem {...props}/>)
  } else if (type == 'oil') {
    return (<OilOrderItem {...props}/>)
  }
}
